import { useQuery } from '@apollo/client';
import { CUSTOM_ACTIVITY_REFS_PER_ENTITY } from '@provision/activities-utils';
import { EntityName, N2NRelationshipName } from '@provision/entity-types';
import {
  EventHubActionOperation,
  EventHubEntityChangeOperation,
  EventHubEntityN2NRelationshipChangeOperation,
} from '@provision/event-hub-utils';
import { QueryHookOptions } from '@provision/graphql-client';
import { isEntityDataChangeEvent } from '@provision/ui-real-time-events';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { uniqueBy } from 'remeda';
import { debounceTime, interval, startWith } from 'rxjs';
import { filter } from 'rxjs/operators';
import { wsEventBus } from 'src/core/realtime-connection';
import { GET_ACTIVITY_QUERY } from 'src/graphql/entities/activity-feed/acitivity-feed.operations';
import { Records } from 'src/graphql/types';
import { sortByDate } from 'src/utils/sorting';

export type ActivityFeedChange = {
  name: string;
  value: string | null;
  previousValue: string | null;
  customType?: string;
  icon?: string;
  field?: string;
  skip?: boolean;
};

export type ActivityFeed = {
  id: string;
  actor: {
    name: string;
  };
  changes: ActivityFeedChange[];
  timestamp: string;
  partnerId: number;
} & (
  | {
      entity: Exclude<EntityName, 'Behavior'>;
      activityType: EventHubEntityChangeOperation;
    }
  | {
      entity: N2NRelationshipName;
      activityType: EventHubEntityN2NRelationshipChangeOperation;
    }
  | {
      entity: 'Behavior';
      activityType?: EventHubActionOperation;
    }
);

export type EntityEvent =
  | {
      entityName: Exclude<EntityName, 'Behavior'>;
      operation?: EventHubEntityChangeOperation;
    }
  | {
      entityName: N2NRelationshipName;
      operation?: EventHubEntityN2NRelationshipChangeOperation;
    }
  | {
      entityName: 'Behavior';
      operation?: EventHubActionOperation;
    };

export type ActivityFeedParams = {
  partnerId: number;
  entityEvents: EntityEvent[];
  identityIds: string[];
  isRoot?: boolean;
  startDate?: Date;
  endDate?: Date;
  referenceName?: EntityName;
};

type ActivityFeedInput = {
  params: ActivityFeedParams;
};

export interface GetActivityQuery {
  getActivityFeed: Records<ActivityFeed>;
}

const ACTIVITY_FEED_FETCH_MORE_DELAY_MS = 5000;
export const POLL_INTERVAL_MS = 60000;

export const useActivityFeed = (params: ActivityFeedParams, skip = false) => {
  const lastFetchedDate = useRef<Dayjs | null>(null);
  const options = useMemo<QueryHookOptions<GetActivityQuery, ActivityFeedInput>>(() => {
    return {
      variables: {
        params,
      },
      skip,
      onCompleted: (_) => {
        if (!lastFetchedDate.current) {
          lastFetchedDate.current = dayjs();
        }
      },
    };
  }, [params, skip]);

  const { data, loading, fetchMore } = useQuery<GetActivityQuery, ActivityFeedInput>(
    GET_ACTIVITY_QUERY,
    options,
  );

  const updateQuery = useCallback(
    (
      previousData: GetActivityQuery,
      { fetchMoreResult }: { fetchMoreResult: GetActivityQuery },
    ) => {
      const records = uniqueBy(
        [...fetchMoreResult.getActivityFeed.records, ...previousData.getActivityFeed.records],
        (i) => i.id,
      ).toSorted(sortByDate('timestamp'));
      return {
        ...fetchMoreResult,
        getActivityFeed: {
          ...fetchMoreResult.getActivityFeed,
          records,
        },
      };
    },
    [],
  );

  useEffect(() => {
    if (options.skip || loading) {
      return;
    }
    const subscription = interval(POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        filter(() => !loading && !options.skip),
      )
      .subscribe(async (_value) => {
        await fetchMore({
          variables: {
            params: {
              ...options.variables?.params,
              startDate: lastFetchedDate.current?.toISOString(),
              endDate: null,
            },
          },
          updateQuery,
        });
        lastFetchedDate.current = dayjs();
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchMore, options.skip, options.variables?.params, updateQuery, loading]);

  useEffect(() => {
    if (options.skip) {
      return;
    }

    const subscription = wsEventBus
      .asObservable()
      .pipe(
        // ViewersPing is a custom operation that doesn't follow the same event structure as others so
        // needs to be filtered out right away.
        filter(
          (event) => !['ViewersPing', 'UserPresencePing'].includes(event.operation as string),
        ),
        filter((event) => {
          let name: string;
          let ids: (string | number)[] = [];

          if (isEntityDataChangeEvent(event)) {
            name = event.data.entityName;
            ids = [event.data.attributes.id];
          } else {
            name = event.data.n2nRelationshipName;
            if (event.operation === 'createN2NRelationship') {
              ids = Object.values(event.data.attributes) as string[];
            } else if (event.operation === 'deleteN2NRelationship') {
              ids = Object.values(event.data.previousAttributes) as string[];
            }
          }

          if (isEntityDataChangeEvent(event) && !params.isRoot && !!params.referenceName) {
            const customEntities = params.entityEvents
              .flatMap((e) => CUSTOM_ACTIVITY_REFS_PER_ENTITY.get(e.entityName as EntityName))
              .filter((e) => e !== undefined);

            return (
              params.entityEvents.some(
                (e) =>
                  name === e.entityName &&
                  (e.operation ? event.operation === e.operation : true),
              ) &&
              customEntities?.some((customEntityGetter) => {
                const attributes =
                  event.operation === 'delete'
                    ? event.data.previousAttributes
                    : event.data.attributes;
                const customEntity = customEntityGetter(attributes);
                if (!customEntity) {
                  return false;
                }
                return params.identityIds.includes(customEntity?.id);
              })
            );
          }

          return params.entityEvents.some(
            (e) =>
              name === e.entityName &&
              (e.operation ? event.operation === e.operation : true) &&
              ids.some((id) => params.identityIds.includes(String(id))),
          );
        }),
        debounceTime(ACTIVITY_FEED_FETCH_MORE_DELAY_MS),
      )
      .subscribe(async (_value) => {
        await fetchMore({
          variables: {
            params: {
              ...options.variables?.params,
              startDate: lastFetchedDate.current?.toISOString(),
              endDate: null,
            },
          },
          updateQuery,
        });
        lastFetchedDate.current = dayjs();
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    fetchMore,
    options.skip,
    options.variables?.params,
    params.entityEvents,
    params.identityIds,
    params.isRoot,
    params.referenceName,
    updateQuery,
  ]);

  return {
    records: data?.getActivityFeed.records,
    loading,
  };
};
